import { usePageTitle } from '../hooks/usePageTitle'
import {
  BrassRule,
  ClubCard,
  ClubLinkButton,
  ClubPage,
  Container,
  Crest,
  Eyebrow,
  SectionHead,
  TierCard,
} from '../components/golf'
import { foundingPlate, membershipTiers } from '../lib/membership'
import type { MembershipTier } from '../lib/membership'

/**
 * The club's front door (route map §4 `/`).
 *
 * One departure from the homepage design card remains: the two member
 * testimonials are replaced by charter extracts, which say the same thing in
 * the club's own voice without inventing members. They come back the moment
 * there are real members to quote.
 *
 * The card's seat counter is BACK, and real. It used to read "87 of 100 seats
 * spoken for" about a club with no members; it now reads the founding plate the
 * shell injects (`Club::FoundingRoll`), and prints nothing at all when the
 * shell said nothing — never a hardcoded hundred.
 */

const PILLARS = [
  {
    numeral: 'I.',
    title: 'The Grounds',
    body: "A virtual course that's always in season and never overbooked. Play a round in the time you've actually got.",
  },
  {
    numeral: 'II.',
    title: 'The Register',
    body: 'A standing roster of members and their rounds — scores kept, handicaps honoured, discretion assured.',
  },
  {
    numeral: 'III.',
    title: 'The Lounge',
    body: "Where the club convenes between rounds. Members only, and the door's never far from the exit.",
  },
]

// The charter's own wording, verbatim from the-club card — the two promises
// the club makes. Kept identical on both pages; if it changes, it changes here
// and in TheClubScreen together.
const CHARTER = [
  {
    article: 'Article the First',
    text: 'That every member belongs — by name, by number, and by right of dues honestly settled.',
  },
  {
    article: 'Article the Second',
    text: 'That every member may slip out whenever they must, without ceremony, explanation, or goodbyes.',
  },
]

const TRUST = [
  { value: '18', label: 'Holes, always open' },
  { value: 'MMXXVI', label: 'Chartered' },
]

/**
 * The three tier CTAs as the homepage card letters them (homepage.html:215,
 * 222, 228). "Apply for membership" is right on the Full Member card where it
 * is wrong in the hero: this button lands on `/join?tier=full`, which IS the
 * application, while the hero's lands on /membership and reads "See the terms"
 * for exactly that reason.
 */
const TIER_CTA_LABEL: Record<MembershipTier['id'], string> = {
  weekender: 'Apply',
  full: 'Apply for membership',
  founding: 'Claim a founding seat',
}

export function GolfLandingScreen() {
  usePageTitle()
  const plate = foundingPlate()
  // Counted, never asserted — and omitted entirely when the shell carries no
  // plate, because "100 founding spots" is a claim once seats are real.
  const stats =
    plate.seats > 0
      ? [{ value: `${plate.taken} of ${plate.seats}`, label: 'Founding seats spoken for' }, ...TRUST]
      : TRUST

  return (
    <ClubPage>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-16 sm:pb-24 sm:pt-22">
        {/* Fairway contours. `preserveAspectRatio="none"` on purpose: with
            `slice` the viewBox is cropped on a hero shorter than 1200×600 and
            the band rides up through the lede, reading as strikethrough. The
            band is also pitched lower than the design card's (which assumes a
            taller hero) so it passes between the copy and the CTAs. */}
        <svg
          viewBox="0 0 1200 600"
          preserveAspectRatio="none"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 size-full opacity-40"
        >
          <path d="M-50 420 Q300 392 620 428 T1250 408" stroke="var(--color-sage)" fill="none" strokeWidth="1.5" />
          <path d="M-50 462 Q300 434 620 470 T1250 450" stroke="var(--color-brass)" fill="none" strokeWidth="1" opacity=".5" />
          <path d="M-50 510 Q320 482 640 520 T1250 500" stroke="var(--color-fairway)" fill="none" strokeWidth="1.5" opacity=".7" />
          <path d="M-50 556 Q320 528 640 566 T1250 546" stroke="var(--color-sage)" fill="none" strokeWidth="1" />
        </svg>

        <Container className="grid items-center gap-12 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <Eyebrow>A private virtual club · Est. MMXXVI</Eyebrow>
            <h1 className="my-5 font-display text-[clamp(2.875rem,6.6vw,5.5rem)] font-medium leading-[1.04] tracking-[-0.018em] text-pine">
              A club for people who&rsquo;ve <em className="italic text-fairway">got to go.</em>
            </h1>
            <p className="mb-8 max-w-[44ch] text-lg text-ink-soft">
              Membership to a golf club that lives wherever you do. The ceremony of the fairway, the
              discretion of the exit — no tee sheet, no dress code you can&rsquo;t leave in.
            </p>

            <div className="flex flex-wrap items-center gap-3.5">
              {/*
                "See the terms", not "Apply for membership": this lands on
                /membership, where a button with those exact words is what
                actually starts an application. Two clicks reading the same made
                the first one look like it had failed.
              */}
              <ClubLinkButton to="/membership">See the terms</ClubLinkButton>
              <ClubLinkButton to="/the-club" variant="ghost">
                Tour the clubhouse
              </ClubLinkButton>
              <span className="ml-1 text-[13px] text-ink-soft">
                <b className="font-semibold text-fairway">Founding members</b> — first hundred only.
              </span>
            </div>

            <dl className="mt-11 flex flex-wrap gap-7 border-t border-brass/35 pt-6">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <dd className="font-display text-[28px] text-pine">{stat.value}</dd>
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
                    {stat.label}
                  </dt>
                </div>
              ))}
            </dl>
          </div>

          {/* The medallion fills its .85fr column at desktop width — 422px
              at 1280, as the card draws it (homepage.html:47,188) — and caps
              at the card's own 320px once the grid stacks (:128). */}
          <Crest monogram className="mx-auto w-full max-w-[320px] lg:max-w-none" />
        </Container>
      </section>

      {/* ── The club ── */}
      <section className="border-y border-brass/30 bg-paper-deep py-21">
        <Container>
          <Eyebrow>The Club</Eyebrow>
          <p className="mt-4 max-w-[24ch] font-display text-[clamp(1.5rem,3vw,2.125rem)] font-medium leading-[1.28] text-pine">
            A members&rsquo; club keeps two promises: that you belong, and that you&rsquo;re welcome
            to slip out whenever you must.
          </p>

          <div className="mt-13 grid gap-8 sm:grid-cols-3">
            {PILLARS.map((pillar) => (
              <div key={pillar.title}>
                <BrassRule short className="mb-4" />
                <span className="font-display text-[15px] text-brass-deep">{pillar.numeral}</span>
                <h3 className="mb-2 mt-2.5 font-display text-xl font-medium text-pine">
                  {pillar.title}
                </h3>
                <p className="text-[14.5px] text-ink-soft">{pillar.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Terms of membership ── */}
      <section className="py-22">
        <Container>
          <SectionHead
            centered
            eyebrow="Terms of Membership"
            title="Choose your standing"
            className="mb-13"
          />
          <div className="grid items-stretch gap-6 lg:grid-cols-3">
            {membershipTiers().map((tier) => (
              <TierCard
                key={tier.id}
                tier={tier}
                featured={tier.id === 'full'}
                to="/join"
                ctaLabel={TIER_CTA_LABEL[tier.id]}
                ctaVariant={tier.id === 'founding' ? 'ink' : undefined}
              />
            ))}
          </div>
        </Container>
      </section>

      {/* ── The charter ── */}
      <section className="border-t border-brass/30 bg-paper-deep py-21">
        <Container>
          <SectionHead
            centered
            eyebrow="From the Charter"
            title="What the club undertakes"
            className="mb-13"
          />
          <div className="grid gap-6 md:grid-cols-2">
            {CHARTER.map((extract) => (
              <ClubCard key={extract.article} className="px-8 py-8">
                <span
                  aria-hidden="true"
                  className="block h-6 font-display text-[56px] leading-[0.6] text-gilt"
                >
                  &ldquo;
                </span>
                <p className="mb-5 mt-1.5 font-display text-[19px] leading-[1.42] text-pine">
                  {extract.text}
                </p>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brass-deep">
                  {extract.article} · The Club Charter
                </p>
              </ClubCard>
            ))}
          </div>
        </Container>
      </section>

      {/* ── The way in ── */}
      <section className="on-pine relative overflow-hidden bg-pine py-24 text-center text-paper">
        <span
          aria-hidden="true"
          className="absolute -left-20 -top-30 size-[340px] rounded-full border border-gilt/25"
        />
        <span
          aria-hidden="true"
          className="absolute -bottom-28 -right-10 size-[260px] rounded-full border border-gilt/25"
        />
        <Container>
          <Eyebrow tone="gilt">Membership is open</Eyebrow>
          <h2 className="mx-auto mb-5 mt-4 max-w-[18ch] font-display text-[clamp(2rem,4.4vw,3.25rem)] font-medium leading-[1.04] text-paper">
            Take your standing at the club.
          </h2>
          <p className="mx-auto mb-8 max-w-[46ch] text-base text-sage">
            A sign-in link, a founding seal, and eighteen holes that keep your hours. The first
            hundred seats carry the charter.
          </p>
          <ClubLinkButton to="/membership">See the terms</ClubLinkButton>
        </Container>
      </section>
    </ClubPage>
  )
}
